import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, output, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, NavigationExtras, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { Cmg1003FileDataQuery, Cmg1003StoreQueryService, SecurityProfileQuery } from '../../../loan-state';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { ConfirmationModalCloseType, Models, SyncType } from '../../../models/global.models';
import { IntegratedLoginContext, FeatureFlagRepository, FeatureName, SessionQuery, SessionStore } from '../../state';
import { ActiveUsersQuery } from '../../state/active-users';
import { Features, PermissionType } from '../../../shared/models/permission/permission.model';
import { GlobalNavService } from '../../../shared/services/global-nav.service';
import { AppSettingsService } from '../../../shared/services/app-settings.service';
import { LeadFormV2Service } from '../../../pipeline/components/lead-form-v2/services/lead-form-v2.service';
import { NavbarService } from '../../../services/navbar.service';
import { navigateWithRefresh } from '../../../shared/utils/router.util';
import { PermissionService } from '../../../shared/services/permission.service';
import { QuickActionsUnsavedChangesService } from '../../../pipeline/components/quick-actions-panel/quick-actions-unsaved-changes.service';
import { SecurityProfileModel, SecurityProfileRole, SecurityProfileStore } from '../../../loan-state/security-profile';
import { SwitchProfilesComponent } from '../switch-profiles/switch-profiles.component';
import { UnsavedChangesDialogResponse } from '../../../shared/models';
import { Upload34Component } from '../upload-3.4/upload-3.4.component';
import { Cmg1003CoverLetterSubmissionsQuery } from '../../../loan-state/cover-letter-submissions/cover-letter-submissions.query';
import { AutoComplete } from 'primeng/autocomplete';
import { RouteUiService } from '../../../pipeline/stores/ui/route-ui.service';
import { ExpandSearchModalComponent } from '../../../pipeline/components/modals/expand-search-modal/expand-search-modal.component';
import { DecimalPipe, NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { Menubar } from 'primeng/menubar';
import { DomHandler } from 'primeng/dom';
import { LibreChatAgent, LibreChatService } from '../../../services/librechat.service';
import { AioService } from '../../../aio/aio.service';
import { environment } from '../../../../environments/environment';
import { VersionNumberComponent } from '../version-number/version-number.component';
import { CmgFormControlDomDirective } from '../../../shared/directives/cmg-form-control-dom.directive';
import { Tooltip } from 'primeng/tooltip';
import { ButtonDirective, Button } from 'primeng/button';
import { CmgHighlightDirective } from '../../../../../../../libs/ui/src/lib/directives/cmg-highlight.directive';
import { Menu } from 'primeng/menu';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Drawer } from 'primeng/drawer';

interface SearchResultGroup {
  totalCount: number;
  groupName: string;
  results: Models.GlobalSearchResult[];
}

type MenuOptions = {
  v1Enabled: boolean;
  hasPartnersPipelineAccess: boolean;
};

@Component({
  selector: 'cmg-clear2-ui-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VersionNumberComponent,
    Menubar,
    ReactiveFormsModule,
    AutoComplete,
    CmgFormControlDomDirective,
    Tooltip,
    ButtonDirective,
    CmgHighlightDirective,
    Menu,
    Button,
    ConfirmDialog,
    Drawer,
    NgTemplateOutlet,
    NgClass,
    RouterLinkActive,
    RouterLink,
    AsyncPipe,
    DecimalPipe,
  ],
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _cmg1003Service = inject(Cmg1003Service);
  private readonly _aioLoanInfoService = inject(AioService);
  private readonly _coverLetterSubmissionsQuery = inject(Cmg1003CoverLetterSubmissionsQuery);
  private readonly _decimalPipe = inject(DecimalPipe);
  private readonly _dialogService = inject(DialogService);
  private readonly _featureFlagRepo = inject(FeatureFlagRepository);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _globalNavService = inject(GlobalNavService);
  private readonly _leadFormV2Service = inject(LeadFormV2Service);
  private readonly _navBarService = inject(NavbarService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _quickActionsPanelService = inject(QuickActionsUnsavedChangesService);
  private readonly _quickActionsService = inject(QuickActionsUnsavedChangesService);
  private readonly _router = inject(Router);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _securityProfileStore = inject(SecurityProfileStore);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _ui = inject(RouteUiService);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _libreChatService = inject(LibreChatService);
  private readonly _appSettingsService = inject(AppSettingsService);
  private readonly _activeUsersQuery = inject(ActiveUsersQuery);

  private _destroyed$ = new Subject();
  dialogRef: DynamicDialogRef;
  searchResults = [];
  showEmptyMessage = false;
  searchRetried = false;
  lastSearchTerm: string = null;
  maxSearchResultsPerType = 5;
  isSidebarVisible = false;
  extraNavItems = this._globalNavService.extraNavItems;

  readonly autocomplete = viewChild<AutoComplete>('autocomplete');

  items$ = new BehaviorSubject<MenuItem[]>([]);
  userProfileMenu$ = new BehaviorSubject<MenuItem[]>([]);
  currentUserInitials$ = this._sessionQuery
    .select(['firstName', 'lastName'])
    .pipe(map((data) => `${data.firstName?.charAt(0)}${data.lastName?.charAt(0)}`.toUpperCase()));
  activeProfile$ = this._securityProfileQuery.selectActive().pipe(filter((t) => !!t));

  libreChatMenuItems$ = new BehaviorSubject<MenuItem[]>([]);
  aiDashboardButtonIntegrationEnabled$ = this._featureFlagRepo.isFeatureEnabled$(FeatureName.AIDashboardButtonIntegration).pipe(startWith(false));
  aiDashboardButtonLink$: Observable<string | null> = this._appSettingsService
    .getApiAppSettingByName$('AIDashboardButtonIntegration:RedirectURLs')
    .pipe(
      startWith(null),
      catchError(() => of(null)),
    );
  aiDashboardMenuItems$ = this.libreChatMenuItems$.pipe(
    map((items) => [
      {
        label: 'AI Tools and Dashboard',
        items: [{ label: 'Clear AI Hub', url: environment.clearAiHubUrl, target: '_blank' }],
      },
      ...items,
    ]),
  );

  // Active Users - only show when in loan context
  activeUsers$ = this._activeUsersQuery.otherUsers$;
  isActiveUsersEnabled$ = this._activeUsersQuery.isEnabled$;
  hasActiveFileContext$ = this._activeUsersQuery.currentFileDataId$.pipe(map((fileDataId) => fileDataId !== null));

  isSecondOrThirdLien = false;

  private _items: MenuItem[] = [];
  private _userProfileMenu = [
    {
      label: 'Switch Profile',
      icon: 'pi pi-sync',
      command: () => {
        this._openSwitchProfile();
      },
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        this._onLogout();
      },
    },
  ];

  readonly logoutClicked = output();

  searchForm = new FormGroup({
    searchTerm: new FormControl(''),
  });

  searchBarTooltip: string;
  searchFailed = false;
  showAllClicked: boolean;
  autoCompleteStyleClass = 'global-search brand-scrollbar';

  ngOnInit(): void {
    /**
     * #25448: PrimeNG Upgrade from 16.4.3 to v17.18.0 | Dropdown Menu Options Appear on Hover Without Clicking
     *
     * This replaces old logic with the this.dirty check that seemed to be preventing this from occurring
     * in past PrimeNG versions on certain devices that were not returning true for isTouchDevice.
     * https://github.com/primefaces/primeng/blob/da7a7ddcf0dadd5f809c5b2aa9994041a5430516/src/app/components/menubar/menubar.ts#L663
     */
    Menubar.prototype.onItemMouseEnter = function (event: any) {
      if (!DomHandler.isTouchDevice()) {
        if (!this.mobileActive && this.dirty) {
          this.onItemChange(event);
        }
      }
    };

    // Tried this in router events - did not work. And since form is already created by this time
    // we don't need to worry about it being enabled/disabled later.
    this._enableDisableNavbarSearch();

    // Executes on every route change.
    this._router.events.pipe(takeUntil(this._destroyed$)).subscribe((ev) => {
      if (ev instanceof NavigationEnd) {
        this._enableDisableNavbarSearch();
      }
    });

    this._featureFlagRepo
      .isFeatureEnabled$(FeatureName.OpportunityMarketingAutomations)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((isAutomatedMarketingEnabled) => {
        if (isAutomatedMarketingEnabled) {
          const hasAutomatedMarketing = this._userProfileMenu.find((menu) => menu.label.includes('Marketing Automations'));
          if (!hasAutomatedMarketing) {
            const automatedMarketingMenu = {
              label: 'Marketing Automations',
              icon: '',
              styleClass: 'marketing-automations-text',
              command: () => {
                this._router.navigate(['/marketing-automations']);
              },
            };
            this._userProfileMenu.splice(1, 0, automatedMarketingMenu);
          }
        }
      });

    combineLatest([
      this._securityProfileQuery.selectActive().pipe(filter((t) => !!t)),
      this._featureFlagRepo.isFeatureEnabled$(FeatureName.ScenarioPricingLegacy),
      this._fileDataQuery.select((f) => f.syncType),
      this._featureFlagRepo.isFeatureEnabled$(FeatureName.PartnerCoBranding),
      this._sessionQuery.select((s) => s.partnersAccess),
      this._featureFlagRepo.isFeatureEnabled$(FeatureName.FeatureFlagManagement),
    ])
      .pipe(
        tap(([profile, isScenarioPricingV1Enabled, syncType, isPartnerCoBrandingEnabled, partnersAccess, isFeatureFlagManagerEnabled]) => {
          this.isSecondOrThirdLien = syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien;
          this._setItems({
            v1Enabled: isScenarioPricingV1Enabled,
            hasPartnersPipelineAccess: isPartnerCoBrandingEnabled || !!partnersAccess?.hasAccess,
          });

          this.items$.next(
            profile.isAdmin
              ? this._items
              : this._items.filter((t) => t.label !== 'Admin' && (t.label !== 'Rates' || this._checkRateSheetPermission(profile))),
          );

          if (isFeatureFlagManagerEnabled) {
            const toolsItem = this._items.find((item) => item.label === 'Tools');

            if (toolsItem) {
              const lockIcon = profile.securityProfileName === SecurityProfileRole.Admin ? '🔓' : '🔒';
              const itemLabel = `Feature Flag Manager ${lockIcon}`;

              const existingIndex = toolsItem.items.findIndex((i) => i.routerLink.includes('feature-flag-manager'));

              const newItem = {
                label: itemLabel,
                routerLink: ['feature-flag-manager'],
              };

              if (existingIndex > -1) {
                toolsItem.items[existingIndex] = newItem;
              } else {
                toolsItem.items.unshift(newItem);
              }
            }
          }

          this.userProfileMenu$.next(this._userProfileMenu);
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.searchForm.controls.searchTerm.valueChanges
      .pipe(
        debounceTime(500),
        tap(() => {
          this.searchResults = [];
          this.autocomplete().hide();
        }),
        filter((searchTerm) => searchTerm && searchTerm.length > 2),
        switchMap((searchTerm) => this._navBarService.executeGlobalSearch(encodeURI(searchTerm), false, this.maxSearchResultsPerType)),
        catchError(() => {
          return of(false);
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe((searchResponse: Models.GlobalSearch) => {
        if (searchResponse && searchResponse.results) {
          this.searchFailed = false;
          const searchResults = [
            this._generateResultGroup(searchResponse, Models.SearchResultType.Loan, 5),
            this._generateResultGroup(searchResponse, Models.SearchResultType.Lead),
          ];
          this.searchResults = searchResults?.filter((group) => group.results?.length > 0);
        } else {
          this.searchFailed = true;
          this.searchResults = [this._generateResultGroup(searchResponse, Models.SearchResultType.Loan, 5)];
        }

        if (!this.searchFailed) {
          let hasMatches = false;

          for (let i = 0; i < this.searchResults.length; i++) {
            if (this.searchResults[i]?.totalCount > 0) {
              hasMatches = true;
              this.searchRetried = false;
              break;
            }
          }

          this.showEmptyMessage = !hasMatches;
          if (this.showEmptyMessage) {
            this.searchResults.length = 0; // The empty message will not display without searchResults being cleared out
          }
        }

        if (this.searchForm.controls.searchTerm.value !== this.lastSearchTerm) {
          this.searchRetried = false;
        }

        this.lastSearchTerm = this.searchForm.controls.searchTerm.value;
        this.autocomplete().show();
        this._cdRef.detectChanges();
      });

    this._assembleLibreChatMenuItems();
  }

  onFocusGlobalSearch() {
    if (this.lastSearchTerm === this.searchForm.controls.searchTerm.value) {
      this.autocomplete().show();
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  get searchTerm() {
    return this.searchForm.controls.searchTerm.value;
  }

  goToLoan(result: Models.GlobalSearchResult) {
    switch (result.type) {
      case Models.SearchResultType.Loan:
        window.open(`${location.origin}/urla2/${result?.loanID}`);
        break;

      case Models.SearchResultType.Lead:
        this._router.navigateByUrl('pipeline/lead-form/' + result?.leadGUID);
        break;

      default:
        break;
    }
  }

  onClearLogoClick(extras?: NavigationExtras) {
    if (this._router.url.indexOf('/pipeline') === -1) {
      this.navigateToPipeline(extras);
    }
  }

  navigateToPipeline(extras?: NavigationExtras) {
    navigateWithRefresh(this._router, '/pipeline', extras);
  }

  onClickShowAllLoans(searchTerm: string, autocomplete: AutoComplete) {
    this.showAllClicked = true;
    this.autoCompleteStyleClass = 'global-search d-none';

    this._navBarService
      .executeGlobalSearch(encodeURI(searchTerm), true)
      .pipe(
        take(1),
        catchError(() => {
          this.showAllClicked = false;
          this.autoCompleteStyleClass = 'global-search brand-scrollbar';
          return of(false);
        }),
      )
      .subscribe((searchResponse: Models.GlobalSearch) => {
        if (searchResponse && searchResponse.results) {
          const loanResults = searchResponse.results.filter((r) => r.type === Models.SearchResultType.Loan);
          if (loanResults && loanResults.length > 0) {
            this.dialogRef = this._dialogService.open(ExpandSearchModalComponent, {
              header: `"${searchTerm}" (${this._decimalPipe.transform(loanResults.length, '1.0-0', 'en-US')}) Results`,
              width: '500px',
              styleClass: 'expand-search-modal',
              contentStyle: { 'max-height': '650px', overflow: 'hidden' },
              data: { searchTerm: this.searchTerm, searchResults: loanResults },
            });

            this.dialogRef.onClose.pipe(take(1)).subscribe(() => {
              this.showAllClicked = false;
              this.autoCompleteStyleClass = 'global-search brand-scrollbar';
            });
          }

          this.searchFailed = false;
          autocomplete.hide();
          this.searchForm.controls.searchTerm.setValue(null);
        } else {
          this.searchFailed = true;
          this.showAllClicked = false;
          this.autoCompleteStyleClass = 'global-search brand-scrollbar';
        }

        this._cdRef.detectChanges();
      });
  }

  closeSidebar(): void {
    this.isSidebarVisible = false;
  }

  retrySearch() {
    const searchTerm = this.searchTerm;
    this.searchForm.controls.searchTerm.setValue(searchTerm); // trigger valueChanges event with previous searchTerm value
    this.searchRetried = true;
  }

  get showTopHeaderUtilities() {
    return this._sessionStore._value().integratedLoginContext !== IntegratedLoginContext.Byte;
  }

  private readonly _checkRateSheetPermission = (profile: SecurityProfileModel) => {
    const ratesheet = profile.featurePermissions?.[Features.RateSheet]?.permission;
    return ratesheet?.view || ratesheet?.update || ratesheet?.delete || ratesheet?.create || false;
  };

  private readonly _goToPipelineByType = (pipelineType: Models.PipelineType) => {
    this.navigateToPipeline({
      state: {
        pipelineType: pipelineType,
      },
    });

    this._quickActionsService
      .isPanelDirty$()
      .pipe(take(1))
      .subscribe((panelDirty) => {
        if (!(panelDirty.isAusDirty || panelDirty.isLoanNotesDirty || panelDirty.isLeadNotesDirty || panelDirty.isDisclosuresDirty)) {
          this._ui.stateChange({ activePipelineType: pipelineType });
        }
      });
  };

  private readonly _openSwitchProfile = () => {
    const isUrla2Route = this._router.url.indexOf('/urla2') >= 0;
    this._processUnsavedChanges()
      .pipe(take(1))
      .subscribe((dialogResponse) => {
        if (dialogResponse !== UnsavedChangesDialogResponse.dismissViaXButton) {
          if (isUrla2Route && dialogResponse === UnsavedChangesDialogResponse.cancelAndContinue) {
            this._storeQuery.rollback();
          }
          this._showSwitchProfileDialog();
        }
      });
  };

  private readonly _openUpload34 = () => {
    this._processUnsavedChanges()
      .pipe(take(1))
      .subscribe((dialogResponse) => {
        if (dialogResponse !== UnsavedChangesDialogResponse.dismissViaXButton) {
          this._showUpload34Dialog();
        }
      });
  };

  private _showSwitchProfileDialog() {
    this.dialogRef = this._dialogService.open(SwitchProfilesComponent, {
      header: 'Switch Profile',
      width: '100%',
      styleClass: 'switch-modal',
      contentStyle: { 'max-height': '600px', overflow: 'auto' },
    });
  }

  private _showUpload34Dialog() {
    this.dialogRef = this._dialogService.open(Upload34Component, {
      header: 'Upload 3.4',
      width: '100%',
      styleClass: 'upload34-modal',
      contentStyle: { 'max-height': '600px', overflow: 'auto' },
    });
  }

  private _onLogout() {
    this._processUnsavedChanges()
      .pipe(take(1))
      .subscribe((dialogResponse) => {
        if (dialogResponse !== UnsavedChangesDialogResponse.dismissViaXButton) {
          this.logoutClicked.emit();
        }
      });
  }

  private _generateResultGroup(searchResponse: Models.GlobalSearch, targetType: Models.SearchResultType, maxCount = 3): SearchResultGroup {
    return {
      groupName: this._mapToGroupName(targetType),
      results: searchResponse ? searchResponse?.results?.filter((result) => result.type === targetType).slice(0, maxCount) : [],
      totalCount: searchResponse?.totalLoanCount,
    };
  }

  private _mapToGroupName(type: Models.SearchResultType): string {
    switch (type) {
      case Models.SearchResultType.Lead:
        return 'Leads';

      case Models.SearchResultType.Loan:
        return 'Loans';

      default:
        return 'Other';
    }
  }

  private _setItems(menuOptions: MenuOptions): void {
    // Configuration for "Create" menu items, based on current profile's organization.
    const itemConfigForProfileOrg = this._permissionService.canAssignLoansToOrg()
      ? {}
      : {
          disabled: true,
          tooltipOptions: {
            tooltipLabel: 'Please switch to a producing branch.',
            tooltipStyleClass: 'disabled-create-tooltip',
          },
        };

    this._items = [
      { label: 'Dashboard', routerLink: '/dashboard' },
      {
        label: 'Pipeline',
        items: [
          {
            label: 'Leads',
            command: () => {
              this._navigateIfNotCurrentlyOnPipelineType(Models.PipelineType.Lead);
            },
          },
          {
            label: 'Applications',
            command: () => {
              this._navigateIfNotCurrentlyOnPipelineType(Models.PipelineType.LoanApplication);
            },
          },
          {
            label: 'All Loans',
            command: () => {
              this._navigateIfNotCurrentlyOnPipelineType(Models.PipelineType.AllLoans);
            },
          },
          {
            label: 'Opportunity Loans',
            command: () => {
              this._navigateIfNotCurrentlyOnPipelineType(Models.PipelineType.ServicingLoan);
            },
          },
          ...(menuOptions.hasPartnersPipelineAccess
            ? [
                {
                  label: 'Partners',
                  command: () => {
                    this._navigateIfNotCurrentlyOnPipelineType(Models.PipelineType.Partners);
                  },
                },
              ]
            : []),
        ],
      },
      {
        label: 'Rates',
        items: this._buildRatesMenu(menuOptions),
      },
      {
        label: 'Tools',
        items: [{ label: 'Fee Worksheet', routerLink: ['./fee-worksheet'] }],
        visible: !this.isSecondOrThirdLien,
      },
      {
        label: 'Admin',
        items: [
          { label: 'User Profile Settings', routerLink: ['clear1/userpermission'], visible: true },
          { label: 'Add/Remove Profiles', routerLink: ['clear1/addRemoveProfiles'] },
          // { label: 'DU Permissions', routerLink: ['clear1/duPermissions'] },
        ],
      },
      {
        label: 'Create',
        icon: 'pi pi-plus-circle',
        styleClass: 'plus-button',
        items: [
          { label: 'Lead', routerLink: '/pipeline/lead-form', ...itemConfigForProfileOrg },
          {
            label: 'Upload 3.4',
            command: () => {
              this._openUpload34();
            },
          },
        ].filter((item) => {
          let hasPermission = true;
          switch (item.label) {
            case 'Lead':
              hasPermission = this._permissionService.hasPermission(Features.Lead, PermissionType.Create);
              break;
            case 'Upload 3.4':
              hasPermission = this._permissionService.hasPermission(Features.Loan, PermissionType.Create);
              break;
            case 'Admin':
              hasPermission = this._permissionService.isAdmin();
              break;
          }
          return hasPermission;
        }),
      },
    ];

    this._featureFlagRepo
      .isFeatureEnabled$(FeatureName.AdminScreen)
      .pipe(
        filter((flag) => flag),
        take(1),
      )
      .subscribe(() => {
        const adminItem = this._items.find((item) => item.label === 'Admin');
        adminItem.items = [
          { label: 'User Profile Settings', routerLink: ['admin/user-profile'], visible: true },
          { label: 'Add/Remove Profiles', routerLink: ['admin/add-remove-profiles'] },
          // { label: 'DU Permissions', routerLink: ['admin/du-permissions'] },
        ];
      });
  }

  private _navigateIfNotCurrentlyOnPipelineType(nextPipeline: Models.PipelineType) {
    if (this._router.url.indexOf('/pipeline') === -1 || this._router.url.includes('/pipeline/lead-form')) {
      this._goToPipelineByType(nextPipeline);
    } else {
      const pipelineUiState = JSON.parse(localStorage.getItem(Models.LocalStorage.PipelineUi));
      if (pipelineUiState && pipelineUiState.activePipelineType !== nextPipeline) {
        this._goToPipelineByType(nextPipeline);
      } else if (!pipelineUiState) {
        this._goToPipelineByType(nextPipeline);
      }
    }
  }

  private _buildRatesMenu(spMenuOptions: MenuOptions): MenuItem[] {
    const scenarioPricingv2RouterLink = 'scenariopricing';
    const scenarioPricingv1RouterLink = 'clear1/scenariopricing';

    const ratesMenu = [];

    if (spMenuOptions.v1Enabled) {
      ratesMenu.push({ label: 'Scenario Pricing', routerLink: [scenarioPricingv1RouterLink] });
    }
    ratesMenu.push({ label: 'Scenario Pricing 2.0', routerLink: [scenarioPricingv2RouterLink] });

    const securityProfileStoreValue: any = this._securityProfileStore.getValue();
    const activeProfile = securityProfileStoreValue?.entities[securityProfileStoreValue.active];
    const dailyRateSheetUrl = activeProfile?.dailyRateSheetUrl ? activeProfile.dailyRateSheetUrl : '/clear1/dailyratesheet';
    const aioRateSheetUrl = activeProfile?.aioRateSheetUrl ? activeProfile.aioRateSheetUrl : 'http://docs.cmgfi.com/retail/aio-ratesheet-retail.pdf';

    ratesMenu.push(
      ...[
        { label: 'Daily Rate Sheet', url: dailyRateSheetUrl, target: '_blank' },
        { label: 'AIO Rate Sheet', url: aioRateSheetUrl, target: '_blank' },
        { label: '2nd/HELOC Rate Sheet', url: 'http://docs.cmgfi.com/retail/CMG2ndsRatesheet.pdf', target: '_blank' },
      ],
    );

    return ratesMenu;
  }

  /**
   * Process unsaved changes has 4 different states
   * 1) User just x'ed the dialog and no longer wants to proceed futher with switch profile/logout - return false
   * 2) Save & continue -> switch profile/logout
   * 3) Cancel & continue -> switch profile/logout
   * 4) No changes were detected.
   * @returns
   */
  private _processUnsavedChanges(): Observable<UnsavedChangesDialogResponse> {
    const resultSubject = new Subject<UnsavedChangesDialogResponse>();
    const isPipelineRoute = this._router.url.indexOf('/pipeline') >= 0;
    const isUrla2Route = this._router.url.indexOf('/urla2') >= 0;
    const isLeadRoute = this._router.url.indexOf('/lead-form') >= 0;
    const isCoverletterRoute = this._router.url.indexOf('/coverletter') >= 0;
    const isAIO = this._router.url.indexOf('/aio') >= 0;
    if (isLeadRoute) {
      this._leadFormV2Service
        .showUnsavedChangesDialog(true)
        .pipe(take(1))
        .subscribe((dialogResponse) => {
          switch (dialogResponse) {
            case UnsavedChangesDialogResponse.cancelAndContinue:
              this._leadFormV2Service.markFormsAsPristineSub.next(true);
              resultSubject.next(dialogResponse);
              break;
            case UnsavedChangesDialogResponse.saveAndContinue:
              this._leadFormV2Service.validateSaveLead$.pipe(take(1)).subscribe((x) => {
                if (x === 'invalid') {
                  resultSubject.next(UnsavedChangesDialogResponse.dismissViaXButton);
                } else {
                  resultSubject.next(dialogResponse);
                }
              });
              break;
            default:
              setTimeout(() => {
                resultSubject.next(dialogResponse);
              });
              break;
          }
        });
    } else if (isPipelineRoute) {
      this._quickActionsPanelService
        .showUnsavedChangesDialog(true)
        .pipe(take(1))
        .subscribe((dialogResponse) => {
          switch (dialogResponse) {
            case UnsavedChangesDialogResponse.cancelAndContinue:
              this._quickActionsPanelService
                .resetQuickActionsPanel()
                .pipe(take(1))
                .subscribe(() => {
                  resultSubject.next(dialogResponse);
                });
              break;
            case UnsavedChangesDialogResponse.saveAndContinue:
              this._quickActionsPanelService
                .saveQuickActionsPanel()
                .pipe(take(1))
                .subscribe(() => {
                  resultSubject.next(dialogResponse);
                });
              break;
            default:
              setTimeout(() => {
                resultSubject.next(dialogResponse);
              });
              break;
          }
        });
    } else if (isCoverletterRoute) {
      if (this._coverLetterSubmissionsQuery.dirtyChecker.isDirty()) {
        this._unsavedChangesDialog(resultSubject);
      } else {
        setTimeout(() => {
          resultSubject.next(UnsavedChangesDialogResponse.noNeedForDialog);
        });
      }
    } else if (isAIO) {
      if (this._aioLoanInfoService?.aioFormHasUnsavedChanges()) {
        this._unsavedChangesDialog(resultSubject);
      } else {
        setTimeout(() => {
          resultSubject.next(UnsavedChangesDialogResponse.noNeedForDialog);
        });
      }
    } else if (isUrla2Route) {
      if (this._storeQuery.isStateDirty()) {
        this._storeQuery
          .statusRequiresFullDisabling$()
          .pipe(take(1))
          .subscribe((statusRequiresFullDisabling) => {
            if (statusRequiresFullDisabling) {
              setTimeout(() => {
                resultSubject.next(UnsavedChangesDialogResponse.noNeedForDialog);
              });
            } else {
              // This is a URLA 2 route, there is at least one dirty store, and the URLA is not disabled; show the Unsaved Changes modal.
              this._unsavedChangesDialog(resultSubject);
            }
          });
      } else {
        setTimeout(() => {
          resultSubject.next(UnsavedChangesDialogResponse.noNeedForDialog);
        });
      }
    } else {
      setTimeout(() => {
        resultSubject.next(UnsavedChangesDialogResponse.noNeedForDialog);
      });
    }
    return resultSubject.asObservable();
  }

  private _unsavedChangesDialog(resultSubject: Subject<UnsavedChangesDialogResponse>) {
    const isUrla2Route = this._router.url.indexOf('/urla2') >= 0;
    const isCoverletterRoute = this._router.url.indexOf('/coverletter') >= 0;
    const isAIO = this._router.url.indexOf('/aio') >= 0;
    const dialogRef = CmgUiUtilities.showUnsavedChangesModal(
      this._dialogService,
      '<div class="d-flex flex-wrap flex-column p-3"><span>Are you sure you want to leave this page without saving?</span><span>All changes will be lost if they are not saved.</span></div>',
      'Save and Continue',
      'Continue without Saving',
      true,
    );
    dialogRef.onClose.subscribe((modalResult: ConfirmationModalCloseType) => {
      if (modalResult === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        if (isCoverletterRoute) {
          this._cmg1003Service.saveCoverLetterSubmissionsIn1003(() => {
            resultSubject.next(UnsavedChangesDialogResponse.saveAndContinue);
          });
        } else if (isAIO) {
          this._aioLoanInfoService.saveAIO(() => {
            resultSubject.next(UnsavedChangesDialogResponse.saveAndContinue);
          });
        } else if (isUrla2Route) {
          this._cmg1003Service.validateAndSave1003(() => {
            resultSubject.next(UnsavedChangesDialogResponse.saveAndContinue);
          });
        }
      } else if (modalResult === ConfirmationModalCloseType.ClosedViaRejectButton) {
        resultSubject.next(UnsavedChangesDialogResponse.cancelAndContinue);
      } else {
        resultSubject.next(UnsavedChangesDialogResponse.dismissViaXButton);
      }
    });
  }

  private _enableDisableNavbarSearch() {
    const allowedUrls = ['/pipeline', '/dashboard']; // Allowed urls.
    const isUrlAllowed = allowedUrls.filter((x) => this._router.url.indexOf(x) >= 0).length > 0;
    const searchInput = this.searchForm?.get('searchTerm');
    if (isUrlAllowed) {
      searchInput?.enable();
      searchInput?.setValue('');
      this.searchBarTooltip = 'Search by Name, Address, Loan Number or Email';
    } else {
      searchInput?.setValue('');
      searchInput?.disable();
      this.searchBarTooltip = 'Good News! Loan search is undergoing improvements. Please navigate to the pipeline screen to enable search.';
    }
  }

  private _assembleLibreChatMenuItems() {
    this._libreChatService
      .getAgents$()
      .pipe(
        takeUntil(this._destroyed$),
        filter((agents) => agents?.length > 0),
        tap((agents) => {
          const groupedMenuItems = [
            {
              label: 'Search',
              items: this._assembleLibreChatSubmenuItems(agents, 'Search: '),
            },
            {
              label: 'Beta Tool',
              items: this._assembleLibreChatSubmenuItems(agents, 'Beta Tool: '),
            },
            {
              label: 'Beta AI Scenario Desk',
              items: this._assembleLibreChatSubmenuItems(agents, 'Beta AI Scenario Desk: '),
            },
          ];

          this.libreChatMenuItems$.next(groupedMenuItems);
        }),
      )
      .subscribe();
  }

  private _assembleLibreChatSubmenuItems(agents: LibreChatAgent[], categoryPrefix: string) {
    return agents
      .filter((agent) => agent.name.startsWith(categoryPrefix))
      .map((agent) => ({
        label: agent.name.replace(categoryPrefix, ''),
        command: () => {
          this._libreChatService.openAgent(agent);
        },
      }));
  }
}
